import React, { useEffect, useRef, useState } from 'react';
import {
    RiMapPinLine as MapPin,
    RiMailLine as Mail,
    RiPhoneLine as Phone,
    RiTimeLine as Time,
    RiFacebookLine as Facebook,
    RiInstagramLine as Instagram,
    RiTiktokFill as Tiktok,
    RiWhatsappLine as Whatsapp,
} from '@remixicon/react';
import BrandStripes from '../../components/common/BrandStripes';
import SocialIcons from '../../components/common/SocialIcons';
import BorderGlow from '../../components/common/BorderGlow';
import HexagonCornerDecoration from '../../components/common/HexagonCornerDecoration';
import { useTheme } from '../../context/ThemeContext';

const colors = {
    primary: '#61257B',
    primaryDark: '#451759',
    secondary: '#C1348B',
    secondaryLight: '#FFD97C',
    accent: '#FFD97C',
    accentDark: '#EA8406',
    moradoPrincipal: '#61257B',
    moradoVariante: '#C1348B',
    naranjaIntenso: '#EA8406',
};

function useInView(threshold = 0.1): [React.RefObject<HTMLElement>, boolean] {
    const ref = useRef<HTMLElement>(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        if (ref.current) {
            const rect = ref.current.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
                setInView(true);
                return;
            }
        }
        const obs = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [threshold]);
    return [ref, inView];
}

type FormState = {
    nombre: string;
    email: string;
    empresa: string;
    telefono: string;
    ciudad: string;
    personas: string;
    mensaje: string;
};

const Contact = () => {
    const { isDark } = useTheme();
    const [heroRef, heroInView] = useInView(0.1);
    const [heroForced, setHeroForced] = useState(false);
    const [contentRef, contentInView] = useInView(0.1);
    const [form, setForm] = useState<FormState>({
        nombre: '',
        email: '',
        empresa: '',
        telefono: '',
        ciudad: '',
        personas: '',
        mensaje: '',
    });
    const [sent, setSent] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setHeroForced(true), 100);
        return () => clearTimeout(t);
    }, []);

    const heroVisible = heroInView || heroForced;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSent(true);
        setTimeout(() => setSent(false), 4000);
        setForm({
            nombre: '',
            email: '',
            empresa: '',
            telefono: '',
            ciudad: '',
            personas: '',
            mensaje: '',
        });
    };

    const [copiedField, setCopiedField] = useState<string | null>(null);

    const handleCopy = (text: string, field: string, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopiedField(field);
        setTimeout(() => setCopiedField(null), 2000);
    };

    const contactItems = [
        {
            id: 'ubicacion',
            num: '01',
            icon: <MapPin size={22} />,
            label: 'Ubicación',
            value: 'Calle 23 de Calacoto, La Paz, Bolivia',
            color: '#c1348b',
            actionText: 'Maps ↗',
            actionUrl: 'https://maps.google.com/?q=Calle+23+de+Calacoto,+La+Paz,+Bolivia',
            isExternal: true,
        },
        {
            id: 'correo',
            num: '02',
            icon: <Mail size={22} />,
            label: 'Correo Electrónico',
            value: 'comarca@gmail.com',
            color: '#C1348B',
            actionText: 'Copiar',
            canCopy: true,
        },
        {
            id: 'whatsapp',
            num: '03',
            icon: <Whatsapp size={22} />,
            label: 'WhatsApp Oficial',
            value: '+591 762 04601',
            color: '#61257b',
            actionText: 'Chatear ↗',
            actionUrl: 'https://api.whatsapp.com/send?phone=59176204601&text=Hola%20Co%20Marca,%20quisiera%20m%C3%A1s%20informaci%C3%B3n',
            isExternal: true,
        },
        {
            id: 'horario',
            num: '04',
            icon: <Time size={22} />,
            label: 'Horario de Atención',
            value: 'Lun – Vie: 8:30 – 19:30 | Sáb: 9:00 – 14:00',
            color: '#EA8406',
            actionText: 'Activo',
            isBadge: true,
        },
    ];

    const socialLinks = [
        { num: '01', Icon: Facebook, url: 'https://www.facebook.com/CoMarcaCoworkconProposito/', label: 'Facebook', color: '#1877F2' },
        { num: '02', Icon: Instagram, url: 'https://www.instagram.com/comarcacowork/', label: 'Instagram', color: '#E1306C' },
        { num: '03', Icon: Tiktok, url: 'https://www.tiktok.com/@co_marca_cowork', label: 'TikTok', color: '#010101' },
        { num: '04', Icon: Whatsapp, url: 'https://api.whatsapp.com/send?phone=59176204601&text=Hola%20desde%20la%20web', label: 'WhatsApp', color: '#25D366' },
    ];

    const inputStyle = {
        width: '100%', padding: '11px 16px',
        border: isDark ? '1.5px solid rgba(193,52,139,0.35)' : '1.5px solid #e5e7eb',
        borderRadius: '12px',
        fontSize: '0.93rem',
        color: isDark ? '#f8fafc' : '#374151',
        fontFamily: "'DM Sans', sans-serif",
        outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s',
        background: isDark ? '#190e2b' : '#fff',
    };

    return (
        <div style={{ fontFamily: "'DM Sans', sans-serif", background: isDark ? '#0b0614' : '#f8fafc', minHeight: '100vh', overflowX: 'clip', transition: 'background 0.3s ease' }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }

        @keyframes blobFloat {
          0%, 100% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; }
          50% { border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; }
        }
        .blob-anim { animation: blobFloat 9s ease-in-out infinite; }

        .fade-up-ct {
          opacity: 0; transform: translateY(28px);
          transition: opacity 0.75s ease, transform 0.75s cubic-bezier(.16,1,.3,1);
        }
        .fade-up-ct.in { opacity: 1; transform: translateY(0); }
        .fade-up-ct.d1 { transition-delay: 0.1s; }
        .fade-up-ct.d2 { transition-delay: 0.2s; }
        .fade-up-ct.d3 { transition-delay: 0.3s; }

        .contact-input:focus {
          border-color: #61257B !important;
          box-shadow: 0 0 0 3px rgba(97,37,123,0.15) !important;
        }
        .contact-input::placeholder { color: #cbd5e1; }

        .contact-info-item { transition: all 0.25s; }
        .contact-info-item:hover { transform: translateX(6px); }

        @media (max-width: 900px) {
          .contact-grid {
            flex-direction: column !important;
            gap: 28px !important;
            align-items: stretch !important;
          }
          .contact-info-card {
            flex: 1 1 auto !important;
            width: 100% !important;
            min-width: 0 !important;
          }
          .contact-form-card {
            width: 100% !important;
            min-width: 0 !important;
            padding: clamp(24px, 4.5vw, 36px) clamp(16px, 3.5vw, 28px) !important;
          }
        }

        @media (max-width: 600px) {
          .contact-hero-section {
            padding: clamp(110px, 18vw, 130px) 16px 44px !important;
          }
          .contact-content-section {
            padding: 32px 14px 64px !important;
          }
          .contact-info-card-header {
            padding: 22px 16px 18px !important;
          }
          .contact-info-card-body {
            padding: 16px 12px 18px !important;
          }
          .emil-card {
            padding: 12px 10px 12px 12px !important;
            gap: 10px !important;
          }
          .emil-card-icon {
            width: 36px !important;
            height: 36px !important;
          }
          .emil-card-value {
            font-size: 0.82rem !important;
          }
          .emil-action-btn {
            padding: 4px 8px !important;
            font-size: 0.68rem !important;
          }
        }

        @media (max-width: 400px) {
          .emil-card {
            padding: 10px 8px 10px 10px !important;
            gap: 8px !important;
          }
          .emil-card-value {
            font-size: 0.78rem !important;
          }
        }
      `}</style>

            {/* ═══ HERO ═══ */}
            <section className="contact-hero-section" style={{
                background: isDark
                    ? 'linear-gradient(135deg, #0b0614 0%, #150a26 50%, #200d36 100%)'
                    : 'linear-gradient(135deg, #fff 0%, #faf5fc 55%, #f5e8fa 100%)',
                padding: 'clamp(110px, 14vw, 140px) clamp(16px, 4vw, 32px) clamp(50px, 8vw, 80px)',
                position: 'relative', overflow: 'hidden',
            }}>
                <div className="blob-anim" style={{
                    position: 'absolute', top: '-80px', right: '-80px',
                    width: '420px', height: '420px',
                    background: isDark ? 'linear-gradient(135deg, rgba(193,52,139,0.18), rgba(97,37,123,0.12))' : 'linear-gradient(135deg, rgba(97,37,123,0.12), rgba(193,52,139,0.08))',
                    pointerEvents: 'none',
                }} />
                {/* Malla hexagonal animada de izquierda a derecha con degradé suave */}
                <HexagonCornerDecoration
                    variant="horizontal-degrade"
                    direction="left-to-right"
                    opacity={isDark ? 0.70 : 0.50}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />

                <div ref={heroRef as React.RefObject<HTMLDivElement>} style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
                    <h1 className={`fade-up-ct d1 ${heroVisible ? 'in' : ''}`} style={{
                        fontSize: 'clamp(2.4rem, 5vw, 3.8rem)',
                        fontWeight: '900', color: isDark ? '#ffffff' : '#0f172a',
                        lineHeight: '1.08', letterSpacing: '-0.025em',
                        marginBottom: '20px',
                    }}>
                        Contácta<span style={{ color: isDark ? '#FFD97C' : '#61257B' }}>nos</span>
                    </h1>

                    <p className={`fade-up-ct d2 ${heroVisible ? 'in' : ''}`} style={{
                        fontSize: '1.05rem', color: isDark ? '#cbd5e1' : '#64748b',
                        maxWidth: '480px', margin: '0 auto',
                        lineHeight: '1.7', fontWeight: '400',
                        textAlign: 'justify',
                    }}>
                        ¿Tienes alguna pregunta? Nos encantaría escucharte y encontrar juntos la mejor solución para ti o tu empresa.
                    </p>

                    <div className={`fade-up-ct d3 ${heroVisible ? 'in' : ''}`} style={{ marginTop: '32px', display: 'flex', justifyContent: 'center' }}>
                        <BrandStripes
                            variant="scroll-prompt"
                            size="md"
                            promptText="Escríbenos directamente"
                            targetId="contact-content-grid"
                        />
                    </div>
                </div>
            </section>

            {/* ═══ CONTENIDO ═══ */}
            <section id="contact-content-grid" className="contact-content-section" style={{ padding: 'clamp(40px, 6vw, 60px) clamp(16px, 4vw, 32px) clamp(60px, 8vw, 100px)', maxWidth: '1200px', margin: '0 auto' }}>
                <div
                    ref={contentRef as React.RefObject<HTMLDivElement>}
                    className="contact-grid"
                    style={{
                        display: 'flex', gap: '28px', alignItems: 'flex-start',
                        opacity: contentInView ? 1 : 0,
                        transform: contentInView ? 'translateY(0)' : 'translateY(32px)',
                        transition: 'all 0.75s cubic-bezier(.16,1,.3,1)',
                    }}
                >
                    {/* ── Info card con BorderGlow ── */}
                    <div style={{ flex: '0 0 430px', minWidth: 0, maxWidth: '100%' }}>
                        <BorderGlow
                            borderRadius={28}
                            backgroundColor={isDark ? '#150d24' : '#ffffff'}
                        >
                            <div className="contact-info-card" style={{
                                width: '100%',
                                background: isDark ? '#150d24' : '#ffffff',
                                borderRadius: '28px',
                                overflow: 'hidden',
                            }}>
                                {/* Header de la card */}
                                <div className="contact-info-card-header" style={{
                                    background: 'linear-gradient(135deg, #420f54 0%, #61257B 50%, #C1348B 100%)',
                                    padding: '30px 28px 26px',
                                    position: 'relative', overflow: 'hidden',
                                }}>
                                    <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '160px', height: '160px', background: 'rgba(255,255,255,0.08)', borderRadius: '50%' }} />
                                    <div style={{ position: 'absolute', bottom: '-50px', left: '-20px', width: '180px', height: '180px', background: 'rgba(255,255,255,0.05)', borderRadius: '50%' }} />
                                    <div style={{ position: 'relative' }}>
                                        <div style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '7px',
                                            background: 'rgba(255,255,255,0.14)', backdropFilter: 'blur(10px)',
                                            borderRadius: '20px', padding: '4px 12px',
                                            fontSize: '0.68rem', fontWeight: '800', color: '#fff',
                                            letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '10px',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                        }}>
                                            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#8b3fa8', boxShadow: '0 0 8px #8b3fa8' }} />
                                            Canales Directos Co Marca
                                        </div>
                                        <h2 style={{ fontSize: '1.35rem', fontWeight: '800', color: '#fff', lineHeight: '1.25', marginBottom: '6px' }}>
                                            Información de Contacto
                                        </h2>
                                        <p style={{ fontSize: '0.86rem', color: 'rgba(255,255,255,0.85)', lineHeight: '1.55' }}>
                                            Conéctate con nosotros por cualquiera de nuestros canales oficiales o visítanos en Calacoto.
                                        </p>
                                    </div>
                                </div>

                                {/* Items de contacto individuales */}
                                <div className="contact-info-card-body" style={{ padding: '24px 22px 28px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                        {contactItems.map((item) => (
                                            <div
                                                key={item.id}
                                                className="emil-card"
                                                style={{
                                                    display: 'flex', alignItems: 'center', gap: '14px',
                                                    padding: '16px 16px 16px 18px',
                                                    background: isDark ? '#1b102e' : '#ffffff',
                                                    borderRadius: '18px',
                                                    border: isDark ? '1.5px solid rgba(193,52,139,0.22)' : '1.5px solid #edf2f7',
                                                    boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.03)',
                                                    position: 'relative',
                                                    cursor: item.actionUrl ? 'pointer' : 'default',
                                                    overflow: 'hidden',
                                                }}
                                                onClick={() => {
                                                    if (item.actionUrl) {
                                                        window.open(item.actionUrl, item.isExternal ? '_blank' : '_self');
                                                    }
                                                }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.transform = 'translateY(-3px)';
                                                    e.currentTarget.style.borderColor = `${item.color}60`;
                                                    e.currentTarget.style.boxShadow = `0 12px 24px -6px ${item.color}22, 0 2px 6px rgba(0,0,0,0.04)`;
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.transform = 'translateY(0)';
                                                    e.currentTarget.style.borderColor = isDark ? 'rgba(193,52,139,0.22)' : '#edf2f7';
                                                    e.currentTarget.style.boxShadow = isDark ? '0 2px 8px rgba(0,0,0,0.25)' : '0 2px 8px rgba(0,0,0,0.03)';
                                                }}
                                            >
                                                {/* Número */}
                                                <span style={{
                                                    position: 'absolute', top: '7px', left: '10px',
                                                    fontSize: '0.62rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                    color: isDark ? '#71717a' : '#94a3b8', fontWeight: '700', letterSpacing: '0.04em',
                                                }}>
                                                    {item.num}
                                                </span>

                                                {/* Icono */}
                                                <div className="emil-card-icon" style={{
                                                    width: '42px', height: '42px', borderRadius: '12px', flexShrink: 0,
                                                    background: `${item.color}18`,
                                                    border: `1.5px solid ${item.color}35`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: item.color,
                                                    marginTop: '4px',
                                                }}>
                                                    {item.icon}
                                                </div>

                                                {/* Información */}
                                                <div style={{ flex: 1, minWidth: 0, marginTop: '4px' }}>
                                                    <div style={{
                                                        fontSize: '0.7rem', fontWeight: '800', color: item.color,
                                                        textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px',
                                                    }}>
                                                        {item.label}
                                                    </div>
                                                    <div className="emil-card-value" style={{
                                                        fontSize: 'clamp(0.82rem, 3.2vw, 0.92rem)', fontWeight: '600', color: isDark ? '#ffffff' : '#0f172a',
                                                        wordBreak: 'break-word', overflowWrap: 'anywhere',
                                                    }}>
                                                        {item.value}
                                                    </div>
                                                </div>

                                                {/* Action pill / Copiar badge */}
                                                {item.canCopy ? (
                                                    <button
                                                        className="emil-action-btn"
                                                        onClick={(e) => handleCopy(item.value, item.id, e)}
                                                        style={{
                                                            flexShrink: 0,
                                                            fontSize: '0.72rem', fontWeight: '700',
                                                            color: copiedField === item.id ? '#10b981' : item.color,
                                                            background: copiedField === item.id ? (isDark ? 'rgba(16,185,129,0.2)' : '#ecfdf5') : `${item.color}15`,
                                                            border: `1px solid ${copiedField === item.id ? '#10b981' : item.color + '40'}`,
                                                            borderRadius: '8px', padding: '5px 10px',
                                                            cursor: 'pointer', transition: 'all 0.2s',
                                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                            marginTop: '4px',
                                                        }}
                                                        title="Copiar al portapapeles"
                                                    >
                                                        {copiedField === item.id ? '✓ Copiado' : 'Copiar ⎘'}
                                                    </button>
                                                ) : item.isBadge ? (
                                                    <span
                                                        className="emil-action-btn"
                                                        style={{
                                                            flexShrink: 0,
                                                            fontSize: '0.68rem', fontWeight: '700',
                                                            color: '#10b981', background: isDark ? 'rgba(16,185,129,0.2)' : '#ecfdf5',
                                                            border: '1px solid rgba(16,185,129,0.4)',
                                                            borderRadius: '8px', padding: '4px 8px',
                                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                                            marginTop: '4px',
                                                        }}
                                                    >
                                                        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#10b981' }} />
                                                        Activo
                                                    </span>
                                                ) : (
                                                    <span
                                                        className="emil-action-btn"
                                                        style={{
                                                            flexShrink: 0,
                                                            fontSize: '0.72rem', fontWeight: '700',
                                                            color: item.color, background: `${item.color}15`,
                                                            border: `1px solid ${item.color}35`,
                                                            borderRadius: '8px', padding: '5px 9px',
                                                            display: 'inline-flex', alignItems: 'center', gap: '2px',
                                                            marginTop: '4px',
                                                        }}
                                                    >
                                                        {item.actionText}
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Redes sociales */}
                                    <div style={{ marginTop: '26px', paddingTop: '22px', borderTop: isDark ? '1.5px solid rgba(193,52,139,0.2)' : '1.5px solid #f1f5f9' }}>
                                        <div style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            marginBottom: '26px',
                                        }}>
                                            <span style={{
                                                fontSize: '0.72rem', fontWeight: '800', color: isDark ? '#cbd5e1' : '#64748b',
                                                textTransform: 'uppercase', letterSpacing: '0.08em',
                                            }}>
                                                Síguenos en Redes
                                            </span>
                                            <span style={{
                                                fontSize: '0.66rem', fontWeight: '700', color: isDark ? '#a1a1aa' : '#94a3b8',
                                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                            }}>
                                                04 CANALES OFICIALES
                                            </span>
                                        </div>

                                        <div style={{ padding: '10px 0 6px' }}>
                                            <SocialIcons buttonSize={52} iconSize={26} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </BorderGlow>
                    </div>

                    {/* ── Formulario con BorderGlow ── */}
                    <div style={{ flex: 1, minWidth: 0, maxWidth: '100%' }}>
                        <BorderGlow
                            borderRadius={28}
                            backgroundColor={isDark ? '#150d24' : '#ffffff'}
                        >
                            <div className="contact-form-card" style={{
                                width: '100%',
                                background: isDark ? '#150d24' : '#fff',
                                borderRadius: '28px',
                                padding: 'clamp(28px, 4.5vw, 40px) clamp(18px, 4vw, 36px)',
                            }}>
                                {/* Label + línea */}
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.14em', textTransform: 'uppercase', color: isDark ? '#FFD97C' : '#61257B' }}>
                                        Formulario
                                    </span>
                                    <div style={{ height: '2px', width: '32px', borderRadius: '2px', background: isDark ? '#FFD97C' : '#61257B' }} />
                                </div>
                                <h2 style={{ fontSize: '1.6rem', fontWeight: '800', color: isDark ? '#ffffff' : '#0f172a', lineHeight: '1.2', letterSpacing: '-0.02em', marginBottom: '28px' }}>
                                    Envíanos un <span style={{ color: isDark ? '#FFD97C' : '#61257B' }}>mensaje</span>
                                </h2>

                                {sent ? (
                                    <div style={{ textAlign: 'center', padding: '48px 0' }}>
                                        <div style={{ fontSize: '3.5rem', marginBottom: '16px' }}>✅</div>
                                        <div style={{ fontSize: '1.2rem', fontWeight: '700', color: isDark ? '#ffffff' : '#0f172a', marginBottom: '8px' }}>¡Mensaje enviado!</div>
                                        <div style={{ color: isDark ? '#cbd5e1' : '#64748b', fontSize: '0.95rem' }}>Nuestro equipo se contactará contigo pronto.</div>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                                        {/* Nombre completo + Correo electrónico */}
                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    Nombre completo <span style={{ color: '#C1348B' }}>*</span>
                                                </label>
                                                <input
                                                    type="text" required
                                                    className="contact-input"
                                                    placeholder="Tu nombre"
                                                    value={form.nombre}
                                                    onChange={e => setForm(p => ({ ...p, nombre: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    Correo electrónico <span style={{ color: '#C1348B' }}>*</span>
                                                </label>
                                                <input
                                                    type="email" required
                                                    className="contact-input"
                                                    placeholder="tu@email.com"
                                                    value={form.email}
                                                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </div>

                                        {/* Nombre de la empresa + Número de teléfono */}
                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    Nombre de la empresa
                                                </label>
                                                <input
                                                    type="text"
                                                    className="contact-input"
                                                    placeholder="Empresa S.A."
                                                    value={form.empresa}
                                                    onChange={e => setForm(p => ({ ...p, empresa: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    Número de teléfono
                                                </label>
                                                <input
                                                    type="tel"
                                                    className="contact-input"
                                                    placeholder="+591 7XX XXXXX"
                                                    value={form.telefono}
                                                    onChange={e => setForm(p => ({ ...p, telefono: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </div>

                                        {/* Ciudad + N° de personas */}
                                        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    Ciudad
                                                </label>
                                                <input
                                                    type="text"
                                                    className="contact-input"
                                                    placeholder="La Paz"
                                                    value={form.ciudad}
                                                    onChange={e => setForm(p => ({ ...p, ciudad: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                            <div style={{ flex: 1, minWidth: '200px' }}>
                                                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                    N° de personas
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="contact-input"
                                                    placeholder="1"
                                                    value={form.personas}
                                                    onChange={e => setForm(p => ({ ...p, personas: e.target.value }))}
                                                    style={inputStyle}
                                                />
                                            </div>
                                        </div>

                                        {/* Mensaje */}
                                        <div>
                                            <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: '600', color: isDark ? '#cbd5e1' : '#374151', marginBottom: '7px' }}>
                                                Mensaje <span style={{ color: '#C1348B' }}>*</span>
                                            </label>
                                            <textarea
                                                required rows={5}
                                                className="contact-input"
                                                placeholder="Cuéntanos en qué podemos ayudarte..."
                                                value={form.mensaje}
                                                onChange={e => setForm(p => ({ ...p, mensaje: e.target.value }))}
                                                style={{ ...inputStyle, resize: 'none' as const }}
                                            />
                                        </div>

                                        <p style={{ fontSize: '0.78rem', color: isDark ? '#94a3b8' : '#64748b', lineHeight: '1.6' }}>
                                            Al hacer clic en el botón, aceptas nuestros{' '}
                                            <a href="/terminos" style={{ color: isDark ? '#FFD97C' : '#61257B', fontWeight: '600', textDecoration: 'none' }}>Términos de servicio</a>
                                            {' '}y nuestra{' '}
                                            <a href="/privacidad" style={{ color: isDark ? '#FFD97C' : '#61257B', fontWeight: '600', textDecoration: 'none' }}>Política de privacidad</a>.
                                        </p>

                                        <button
                                            type="submit"
                                            style={{
                                                background: 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)',
                                                color: '#fff', border: 'none', borderRadius: '14px',
                                                padding: '14px', fontWeight: '700', fontSize: '0.97rem',
                                                cursor: 'pointer', fontFamily: "'DM Sans', sans-serif",
                                                boxShadow: '0 6px 20px rgba(97,37,123,0.35)',
                                                transition: 'all 0.2s', letterSpacing: '0.01em',
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 28px rgba(97,37,123,0.48)'; }}
                                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(97,37,123,0.35)'; }}
                                        >
                                            Enviar mensaje →
                                        </button>
                                    </form>
                                )}
                            </div>
                        </BorderGlow>
                    </div>
                </div>

                {/* Barra decorativa */}
                <div style={{ marginTop: '64px', display: 'flex', justifyContent: 'center' }}>
                    <BrandStripes size="md" />
                </div>
            </section>
        </div>
    );
};

export default Contact;